import React from "react";
import { useLocation } from "wouter";
import { Plus, Loader2, ChevronRight } from "lucide-react";
import {
  useListPaymentRuns,
  useCreatePaymentRun,
  getListPaymentRunsQueryKey,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { formatZAR } from "@/lib/formatters";

export function PaymentRunsList() {
  const { data: runs, isLoading, error } = useListPaymentRuns();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();
  const createRun = useCreatePaymentRun();

  async function handleCreate() {
    try {
      const run = await createRun.mutateAsync({ data: {} });
      await queryClient.invalidateQueries({ queryKey: getListPaymentRunsQueryKey() });
      setLocation(`/payment-runs/${run.id}`);
    } catch {
      /* surfaced via button state */
    }
  }

  if (isLoading)
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-gray-200 w-1/4 rounded mb-6"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  if (error || !runs)
    return <div className="p-6 text-red-500">Failed to load payment runs.</div>;

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg text-[#0B132B]">Payment Runs</CardTitle>
        <button
          onClick={handleCreate}
          disabled={createRun.isPending}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-colors ${
            createRun.isPending
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-[#E6007E] text-white hover:bg-opacity-90 shadow-sm"
          }`}
        >
          {createRun.isPending ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Plus size={16} />
          )}
          New payment run
        </button>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader className="bg-gray-50">
            <TableRow>
              <TableHead>Reference</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Total Value</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.map((run) => (
              <TableRow
                key={run.id}
                className="cursor-pointer"
                onClick={() => setLocation(`/payment-runs/${run.id}`)}
                data-testid={`payment-run-${run.id}`}
              >
                <TableCell className="font-medium">{run.reference}</TableCell>
                <TableCell>
                  {run.createdAt ? new Date(run.createdAt).toLocaleDateString() : "-"}
                </TableCell>
                <TableCell className="text-right">
                  {formatZAR(run.totalPayoutValueCents)}
                </TableCell>
                <TableCell>
                  <StatusBadge status={run.status} />
                </TableCell>
                <TableCell className="text-gray-300">
                  <ChevronRight size={16} />
                </TableCell>
              </TableRow>
            ))}
            {runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center p-8 text-gray-500">
                  No payment runs found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
